import { useLayoutEffect, useRef, useState } from "react";
import type { HeadState } from "./model";

const FRAME_INTERPOLATION_MS = 250;

export interface InterpolatedHeadFrame {
  volumeMl: number;
  elapsedMs: number;
  visibleFrames: number;
}

type AnimationTarget = InterpolatedHeadFrame & { live: boolean };

interface Animation extends InterpolatedHeadFrame {
  target: InterpolatedHeadFrame;
  startMs: number;
}

const emptyFrame: InterpolatedHeadFrame = {
  volumeMl: 0,
  elapsedMs: 0,
  visibleFrames: 0,
};

function targetFor(head: HeadState): AnimationTarget {
  return {
    live: head.detectorActive || head.demoActive,
    volumeMl: head.volumeMl,
    elapsedMs: head.elapsedMs,
    visibleFrames: head.trace.points.length,
  };
}

function interpolate(animation: Animation, nowMs: number): InterpolatedHeadFrame {
  const progress = Math.min(1, Math.max(0, (nowMs - animation.startMs) / FRAME_INTERPOLATION_MS));
  return {
    volumeMl: animation.volumeMl + (animation.target.volumeMl - animation.volumeMl) * progress,
    elapsedMs: animation.elapsedMs + (animation.target.elapsedMs - animation.elapsedMs) * progress,
    visibleFrames: animation.visibleFrames + (animation.target.visibleFrames - animation.visibleFrames) * progress,
  };
}

/** Keeps the live metric cards and graph on the controller's 250 ms frame cadence. */
export function useFrameInterpolation(heads: Record<1 | 2, HeadState>): Record<1 | 2, InterpolatedHeadFrame> {
  const animations = useRef<Partial<Record<1 | 2, Animation>>>({});
  const targets = useRef<Partial<Record<1 | 2, AnimationTarget>>>({});
  const raf = useRef<number | undefined>(undefined);
  const [displayed, setDisplayed] = useState<Record<1 | 2, InterpolatedHeadFrame>>({ 1: emptyFrame, 2: emptyFrame });

  useLayoutEffect(() => {
    const nowMs = performance.now();
    let needsAnimation = false;
    const nextDisplayed = {} as Record<1 | 2, InterpolatedHeadFrame>;

    for (const id of [1, 2] as const) {
      const target = targetFor(heads[id]);
      const previousTarget = targets.current[id];
      const previousAnimation = animations.current[id];
      targets.current[id] = target;

      if (!target.live) {
        const settled = { volumeMl: target.volumeMl, elapsedMs: target.elapsedMs, visibleFrames: target.visibleFrames };
        animations.current[id] = { ...settled, target: settled, startMs: nowMs };
        nextDisplayed[id] = settled;
        continue;
      }

      const current = previousAnimation ? interpolate(previousAnimation, nowMs) : emptyFrame;
      const isNewCycle = !previousTarget
        || !previousTarget.live
        || target.volumeMl < previousTarget.volumeMl
        || target.elapsedMs < previousTarget.elapsedMs
        || target.visibleFrames < previousTarget.visibleFrames;
      const start = isNewCycle
        ? { volumeMl: 0, elapsedMs: 0, visibleFrames: Math.max(0, target.visibleFrames - 1) }
        : current;
      const targetFrame = { volumeMl: target.volumeMl, elapsedMs: target.elapsedMs, visibleFrames: target.visibleFrames };
      const animation = { ...start, target: targetFrame, startMs: nowMs };
      animations.current[id] = animation;
      nextDisplayed[id] = start;
      needsAnimation ||= start.volumeMl !== targetFrame.volumeMl
        || start.elapsedMs !== targetFrame.elapsedMs
        || start.visibleFrames !== targetFrame.visibleFrames;
    }

    setDisplayed(nextDisplayed);
    if (raf.current !== undefined) cancelAnimationFrame(raf.current);
    if (!needsAnimation) return;

    const renderFrame = (now: number): void => {
      const next = {} as Record<1 | 2, InterpolatedHeadFrame>;
      let stillAnimating = false;
      for (const id of [1, 2] as const) {
        const animation = animations.current[id]!;
        next[id] = interpolate(animation, now);
        stillAnimating ||= now - animation.startMs < FRAME_INTERPOLATION_MS;
      }
      setDisplayed(next);
      raf.current = stillAnimating ? requestAnimationFrame(renderFrame) : undefined;
    };
    raf.current = requestAnimationFrame(renderFrame);
    return () => {
      if (raf.current !== undefined) cancelAnimationFrame(raf.current);
      raf.current = undefined;
    };
  }, [heads]);

  return displayed;
}
